
import img0 from './EnableCloudSubscription.png';
  
const content = `# Custom Cloud Subscription Guide

## Objective

***
The objective of this document is to guide OnData customers on how to use their own cloud subscription with the OnData Platform. When the custom cloud subscription option is enabled, all cloud resources 
will be stored in a designated resource group of the custom cloud subscription, instead of the default cloud subscription owned and managed by OnData.

Currently, only the **Azure US Government** cloud platform and the **Azure (Global)** cloud platform are supported.
***

## Prerequisites

- An Azure cloud subscription and an Azure Active Directory (AAD) user account with access to manage Access control (IAM).
- An Azure Active Directory user account with either the **Global Administrator** role or the **Privileged Role Administrator** role.

## Main Steps

- Create a resource group for the OnData Platform to use
- Configure Cloud Subscription at the OnData Portal
- Grant admin consent to the OnData service principal
- Assign resource group access to the OnData service principal
- Test cloud subscription configuration
- Submit cloud subscription configuration

### Create a resource group for the OnData Platform to use

Create a resource group in your cloud subscription for the OnData Platform to use. The OnData Platform will store all of your contents and resources in this resource group.

Supported regions:

- Azure US Government: Currently, only **US Gov Texas** is supported.
- Azure (Global): Currently, only **South Central US** is supported.

### Configure Cloud Subscription at the OnData Portal

- Click on the **Settings** icon on the right side of the top navigation bar.
- Select **Organization** to navigate to the **Organization Settings** page.
- Select the **Cloud Subscription** tab and toggle the **Use My Subscription** button.
- Fill in the **Tenant Id** and **Subscription Id** fields. You can find the Tenant ID under your Azure Active Directory properties and locate your Subscription ID on the Overview page of the created resource group.
- Once all information above is completed, the **Cloud Platform Consent URL** will be automatically generated. Copy the **Cloud Platform Consent URL** and send it to your cloud administrative user with either the 
**Global Administrator** role or the **Privileged Role Administrator** role to grant admin consent to the OnData service principal.

![Enable Cloud Subscription](${ img0 })

### Grant admin consent to the OnData service principal

- Open the **Cloud Platform Consent URL** in a browser
- Sign in with a user role of either **Global Administrator** or **Privileged Role Administrator**
- Grant admin consent to the OnData service principal
- Verify the admin consent result in the **Enterprise Applications** page

### Assign resource group access to the OnData service principal

- Sign into the Azure Portal and navigate to the resource group created above
- Navigate to the **Access control (IAM)** page of the resource group
- Assign the following roles to the OnData service principal
  - Virtual Machine Contributor
  - Network Contributor
  - Storage Account Contributor
  - Key Vault Contributor
  - Monitoring Reader

### Test cloud subscription configuration

After the above configuration steps are completed, jump back to the Cloud Subscription page at the OnData Portal. Click on the **Test Configuration** button to test the connectivity and configuration.

### Submit cloud subscription configuration

If the cloud subscription configuration is verified at the step above, you can persist such configuration by clicking on the **Update Cloud Subscription** button and waiting for the process to complete.

## How to disconnect custom cloud subscription

You can take any of the steps below to disconnect your cloud subscription from the OnData Platform. If you do not plan to use the custom cloud subscription anymore, please take all of the steps below.
- Toggle the **Use My Subscription** button at the OnData Portal to disconnect the cloud subscription
- Remove the roles assigned to the OnData service principal from the **Access control (IAM)** page of the designated resource group
- Remove the admin consent granted to the OnData service principal from the **Enterprise applications** page.

After the cloud subscription is disconnected from the OnData Platform, all cloud resources previously created by the OnData Platform will remain in the designated resource group. The OnData Platform 
will no longer be able to create or manage any resources in your cloud subscription. If you want to continue to use the OnData Platform without connecting your own cloud subscription, all future contents 
and resources will be stored and managed in an OnData resource group dedicated to your organization. In that case, OnData will invoice your organization the cloud resource consumption in addition to the annual 
subscription cost of the OnData Platform.`
  
export default content;
